import {
  OPEN_FILTERS_KEY,
  SELECTED_COLUMNS_KEY,
  TableNamesEnum,
  TableNamesKeys,
} from '@/common/constants/tables';

const PERSISTED_TABLE_STATE_VERSION_KEY = 'persisted-table-state-version';
const PERSISTED_TABLE_STATE_VERSION = 1;

const STATUS_FILTER_FIELD = 'entityStatus';
const RETIRED_STATUS_FILTER_VALUE = 'un-enrolled';

type ColumnIdentityChanges = {
  renamedIdentities?: Record<string, string>;
  addedIdentities?: string[];
};

const enrollmentColumnsOnCourseUsers: ColumnIdentityChanges = {
  renamedIdentities: { 'enrollement.unEnrolledAt': 'enrollment.unEnrolledAt' },
  addedIdentities: ['enrollment.unEnrolledBy'],
};

const COLUMN_IDENTITY_CHANGES: Partial<
  Record<TableNamesEnum, ColumnIdentityChanges>
> = {
  [TableNamesEnum.COURSE_USERS]: enrollmentColumnsOnCourseUsers,
  [TableNamesEnum.MATERIAL_USERS]: enrollmentColumnsOnCourseUsers,
  [TableNamesEnum.USER_COURSES_LIST]: {
    addedIdentities: ['enrollment.unEnrolledAt', 'enrollment.unEnrolledBy'],
  },
};

type PersistedFilterValue = {
  value?: unknown;
  filter?: Record<string, unknown>;
};

type PersistedTableFilters = Record<string, PersistedFilterValue>;

const parsePersistedRecord = <TEntry>(
  storageKey: string,
): Record<string, TEntry> | null => {
  const stored = localStorage.getItem(storageKey);

  if (!stored) {
    return null;
  }

  try {
    const parsed = JSON.parse(stored);

    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, TEntry>)
      : null;
  } catch {
    return null;
  }
};

const columnIdentityChangesFor = (
  storageKey: string,
): ColumnIdentityChanges | undefined => {
  const tableName = (
    Object.keys(COLUMN_IDENTITY_CHANGES) as TableNamesEnum[]
  ).find((name) => storageKey.startsWith(`${TableNamesKeys[name]}-`));

  return tableName ? COLUMN_IDENTITY_CHANGES[tableName] : undefined;
};

const migrateSelectedColumns = () => {
  const persisted = parsePersistedRecord<string[]>(SELECTED_COLUMNS_KEY);

  if (!persisted) {
    return;
  }

  let hasChanges = false;

  const migrated = Object.keys(persisted).reduce<Record<string, string[]>>(
    (accumulator, storageKey) => {
      const identities = persisted[storageKey];
      const changes = columnIdentityChangesFor(storageKey);

      if (!changes || !Array.isArray(identities)) {
        accumulator[storageKey] = identities;

        return accumulator;
      }

      const renamedIdentities = changes.renamedIdentities ?? {};
      const resolvedIdentities = identities
        .filter((identity): identity is string => typeof identity === 'string')
        .map((identity) => renamedIdentities[identity] ?? identity);

      const nextIdentities = Array.from(
        new Set([...resolvedIdentities, ...(changes.addedIdentities ?? [])]),
      );

      if (JSON.stringify(nextIdentities) !== JSON.stringify(identities)) {
        hasChanges = true;
      }

      accumulator[storageKey] = nextIdentities;

      return accumulator;
    },
    {},
  );

  if (hasChanges) {
    localStorage.setItem(SELECTED_COLUMNS_KEY, JSON.stringify(migrated));
  }
};

const withoutStaleOptions = (
  filter: PersistedFilterValue['filter'],
): PersistedFilterValue['filter'] => {
  if (!filter || !('options' in filter)) {
    return filter;
  }

  const next = { ...filter };

  delete next.options;

  return next;
};

const migrateStatusFilter = (
  statusFilter: PersistedFilterValue,
): PersistedFilterValue | null => {
  if (Array.isArray(statusFilter.value)) {
    const remainingValues = statusFilter.value.filter(
      (value) => value !== RETIRED_STATUS_FILTER_VALUE,
    );

    if (!remainingValues.length) {
      return null;
    }

    return {
      ...statusFilter,
      value: remainingValues,
      filter: withoutStaleOptions(statusFilter.filter),
    };
  }

  if (statusFilter.value === RETIRED_STATUS_FILTER_VALUE) {
    return null;
  }

  return {
    ...statusFilter,
    filter: withoutStaleOptions(statusFilter.filter),
  };
};

const migrateOpenFilters = () => {
  const persisted =
    parsePersistedRecord<PersistedTableFilters>(OPEN_FILTERS_KEY);

  if (!persisted) {
    return;
  }

  let hasChanges = false;

  const migrated = Object.keys(persisted).reduce<
    Record<string, PersistedTableFilters>
  >((accumulator, storageKey) => {
    const tableFilters = persisted[storageKey];
    const statusFilter = tableFilters?.[STATUS_FILTER_FIELD];

    if (!tableFilters || typeof tableFilters !== 'object' || !statusFilter) {
      accumulator[storageKey] = tableFilters;

      return accumulator;
    }

    const nextStatusFilter = migrateStatusFilter(statusFilter);

    if (JSON.stringify(nextStatusFilter) === JSON.stringify(statusFilter)) {
      accumulator[storageKey] = tableFilters;

      return accumulator;
    }

    hasChanges = true;

    const nextTableFilters = { ...tableFilters };

    if (nextStatusFilter) {
      nextTableFilters[STATUS_FILTER_FIELD] = nextStatusFilter;
    } else {
      delete nextTableFilters[STATUS_FILTER_FIELD];
    }

    if (Object.keys(nextTableFilters).length) {
      accumulator[storageKey] = nextTableFilters;
    }

    return accumulator;
  }, {});

  if (hasChanges) {
    localStorage.setItem(OPEN_FILTERS_KEY, JSON.stringify(migrated));
  }
};

let hasRunMigrations = false;

const runPersistedTableStateMigrations = () => {
  if (hasRunMigrations) {
    return;
  }

  hasRunMigrations = true;

  try {
    const persistedVersion = Number(
      localStorage.getItem(PERSISTED_TABLE_STATE_VERSION_KEY),
    );

    if (
      Number.isFinite(persistedVersion) &&
      persistedVersion >= PERSISTED_TABLE_STATE_VERSION
    ) {
      return;
    }

    migrateSelectedColumns();
    migrateOpenFilters();

    localStorage.setItem(
      PERSISTED_TABLE_STATE_VERSION_KEY,
      String(PERSISTED_TABLE_STATE_VERSION),
    );
  } catch {
    return;
  }
};

export const readPersistedSelectedColumns = (): Record<
  string,
  string[]
> | null => {
  runPersistedTableStateMigrations();

  return parsePersistedRecord<string[]>(SELECTED_COLUMNS_KEY);
};

export const readPersistedOpenFilters = <TFilterValues>(): Record<
  string,
  TFilterValues
> | null => {
  runPersistedTableStateMigrations();

  return parsePersistedRecord<TFilterValues>(OPEN_FILTERS_KEY);
};
